import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageCircle, X, Send, Bot, User, AlertTriangle,
  Phone, CheckCheck, Loader2, Search, MoreVertical, Smile, Paperclip, Mic
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface Conversation {
  id: string;
  remote_phone: string;
  status: string;
  last_message_at: string | null;
  human_takeover_at: string | null;
  created_at: string;
  metadata: any;
  lastMessage?: string;
}

interface Message {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  media_type: string | null;
  media_url: string | null;
  created_at: string;
}

interface WhatsAppChatWidgetProps {
  restaurantId: string;
}

const formatPhone = (phone: string) => {
  const clean = phone.replace(/\D/g, "");
  if (clean.length === 13) return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`;
  if (clean.length === 12) return `+${clean.slice(0, 2)} (${clean.slice(2, 4)}) ${clean.slice(4, 8)}-${clean.slice(8)}`;
  return phone;
};

const formatTime = (date: string) => {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return d.toLocaleDateString("pt-BR", { weekday: "short" });
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export default function WhatsAppChatWidget({ restaurantId }: WhatsAppChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadTakeovers, setUnreadTakeovers] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "attention" | "bot">("all");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadConversations = useCallback(async () => {
    const { data } = await supabase
      .from("whatsapp_chatbot_conversations")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("last_message_at", { ascending: false })
      .limit(100);
    if (data) {
      setConversations(data as any);
      setUnreadTakeovers((data as any).filter((c: Conversation) => c.status === "human_active").length);
    }
  }, [restaurantId]);

  const loadMessages = useCallback(async (convId: string) => {
    setLoading(true);
    const { data } = await supabase
      .from("whatsapp_chatbot_messages")
      .select("*")
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true })
      .limit(500);
    if (data) setMessages(data as any);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) loadConversations();
  }, [open, loadConversations]);

  useEffect(() => {
    if (!open) return;
    const channel = supabase
      .channel("wa-convs-widget")
      .on("postgres_changes", { event: "*", schema: "public", table: "whatsapp_chatbot_conversations", filter: `restaurant_id=eq.${restaurantId}` }, () => loadConversations())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [open, restaurantId, loadConversations]);

  useEffect(() => {
    if (!selectedConv) return;
    const channel = supabase
      .channel(`wa-msgs-${selectedConv.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "whatsapp_chatbot_messages", filter: `conversation_id=eq.${selectedConv.id}` }, (payload: any) => {
        setMessages(prev => [...prev, payload.new as Message]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (selectedConv) inputRef.current?.focus();
  }, [selectedConv]);

  const handleSelectConv = (conv: Conversation) => {
    setSelectedConv(conv);
    loadMessages(conv.id);
  };

  const handleTakeover = async () => {
    if (!selectedConv) return;
    await supabase.from("whatsapp_chatbot_conversations").update({ status: "human_active", human_takeover_at: new Date().toISOString() } as any).eq("id", selectedConv.id);
    setSelectedConv({ ...selectedConv, status: "human_active" });
    loadConversations();
    toast.success("Você assumiu a conversa.");
  };

  const handleReleaseToBot = async () => {
    if (!selectedConv) return;
    await supabase.from("whatsapp_chatbot_conversations").update({ status: "bot_active", human_takeover_at: null } as any).eq("id", selectedConv.id);
    setSelectedConv({ ...selectedConv, status: "bot_active" });
    loadConversations();
    toast.success("Bot reativado.");
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("whatsapp-send", {
        body: { restaurant_id: restaurantId, phone: selectedConv.remote_phone, message: newMessage.trim() },
      });
      if (error) throw error;
      await supabase.from("whatsapp_chatbot_messages").insert({ conversation_id: selectedConv.id, role: "assistant", content: newMessage.trim() } as any);
      await supabase.from("whatsapp_chatbot_conversations").update({ last_message_at: new Date().toISOString() } as any).eq("id", selectedConv.id);
      setNewMessage("");
    } catch (err: any) {
      toast.error("Erro ao enviar: " + (err.message || "tente novamente"));
    } finally {
      setSending(false);
    }
  };

  const filteredConvs = conversations.filter(c => {
    if (filter === "attention" && c.status !== "human_active") return false;
    if (filter === "bot" && c.status !== "bot_active") return false;
    if (searchQuery) return c.remote_phone.includes(searchQuery);
    return true;
  });

  const attentionCount = conversations.filter(c => c.status === "human_active").length;

  // Group messages by date
  const groupedMessages = messages.reduce<{ date: string; msgs: Message[] }[]>((acc, msg) => {
    const dateStr = new Date(msg.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    const last = acc[acc.length - 1];
    if (last && last.date === dateStr) {
      last.msgs.push(msg);
    } else {
      acc.push({ date: dateStr, msgs: [msg] });
    }
    return acc;
  }, []);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 bg-[#25D366] text-white hover:bg-[#20bd5a]"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && unreadTakeovers > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF3B30] text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadTakeovers}
          </span>
        )}
      </button>

      {/* Full WhatsApp-style Panel */}
      {open && (
        <div className="fixed inset-4 md:inset-8 lg:bottom-24 lg:right-6 lg:left-auto lg:top-auto lg:w-[900px] lg:h-[700px] z-[59] rounded-xl shadow-2xl overflow-hidden flex animate-in zoom-in-95 fade-in duration-200"
          style={{ border: "1px solid #2a2f32" }}
        >
          {/* === LEFT SIDEBAR — Conversation List === */}
          <div className="w-full lg:w-[340px] flex flex-col shrink-0" style={{ background: "#111b21" }}>
            {/* Sidebar Header */}
            <div className="h-[60px] flex items-center justify-between px-4" style={{ background: "#202c33" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2a3942] flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#aebac1]" />
                </div>
                <span className="text-[#e9edef] font-medium text-sm">Conversas</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#2a3942] transition-colors">
                  <MoreVertical className="w-5 h-5 text-[#aebac1]" />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-3 py-2" style={{ background: "#111b21" }}>
              <div className="flex items-center gap-3 rounded-lg px-3 py-1.5" style={{ background: "#202c33" }}>
                <Search className="w-4 h-4 text-[#8696a0]" />
                <input
                  type="text"
                  placeholder="Pesquisar ou começar uma nova conversa"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="bg-transparent text-[#d1d7db] text-sm placeholder-[#8696a0] outline-none flex-1"
                />
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1.5 px-3 pb-2">
              {[
                { key: "all" as const, label: "Tudo" },
                { key: "attention" as const, label: `Atenção ${attentionCount > 0 ? attentionCount : ""}` },
                { key: "bot" as const, label: "Bot" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                    filter === tab.key
                      ? "bg-[#00a884] text-[#111b21]"
                      : "bg-[#202c33] text-[#8696a0] hover:bg-[#2a3942]"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Conversation List */}
            <ScrollArea className="flex-1">
              {filteredConvs.length === 0 && (
                <p className="text-center text-[#8696a0] text-sm py-12">Nenhuma conversa</p>
              )}
              {filteredConvs.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConv(conv)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-3 transition-colors border-b border-[#222d34] hover:bg-[#202c33]",
                    selectedConv?.id === conv.id && "bg-[#2a3942]"
                  )}
                >
                  {/* Avatar */}
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center shrink-0",
                    conv.status === "human_active" ? "bg-[#f7c948]/20" : "bg-[#2a3942]"
                  )}>
                    {conv.status === "human_active" ? (
                      <AlertTriangle className="w-5 h-5 text-[#f7c948]" />
                    ) : (
                      <User className="w-5 h-5 text-[#8696a0]" />
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <p className="text-[#e9edef] text-sm font-normal truncate">{formatPhone(conv.remote_phone)}</p>
                      <span className={cn(
                        "text-[11px] shrink-0 ml-2",
                        conv.status === "human_active" ? "text-[#00a884]" : "text-[#8696a0]"
                      )}>
                        {conv.last_message_at ? formatTime(conv.last_message_at) : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {conv.status === "human_active" ? (
                        <span className="text-xs text-[#f7c948] flex items-center gap-1 truncate">
                          <AlertTriangle className="w-3 h-3 shrink-0" />
                          Aguardando atendente
                        </span>
                      ) : (
                        <span className="text-xs text-[#8696a0] flex items-center gap-1 truncate">
                          <Bot className="w-3 h-3 shrink-0" />
                          Bot respondendo
                        </span>
                      )}
                      {conv.status === "human_active" && (
                        <span className="ml-auto w-5 h-5 rounded-full bg-[#00a884] text-[#111b21] text-[10px] flex items-center justify-center font-bold shrink-0">!</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </ScrollArea>
          </div>

          {/* === RIGHT SIDE — Chat Area === */}
          <div className={cn("flex-1 flex-col", selectedConv ? "flex" : "hidden lg:flex")} style={{ background: "#0b141a" }}>
            {selectedConv ? (
              <>
                {/* Chat Header */}
                <div className="h-[60px] flex items-center gap-3 px-4 shrink-0" style={{ background: "#202c33" }}>
                  {/* Back button mobile */}
                  <button onClick={() => { setSelectedConv(null); setMessages([]); }} className="lg:hidden mr-1">
                    <svg className="w-5 h-5 text-[#aebac1]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                  <div className="w-10 h-10 rounded-full bg-[#2a3942] flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-[#aebac1]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#e9edef] text-sm font-medium truncate">{formatPhone(selectedConv.remote_phone)}</p>
                    <p className="text-[11px] text-[#8696a0] flex items-center gap-1">
                      {selectedConv.status === "human_active" ? (
                        <><User className="w-3 h-3" /> Você está atendendo</>
                      ) : (
                        <><Bot className="w-3 h-3" /> Bot ativo</>
                      )}
                    </p>
                  </div>
                  {selectedConv.status === "bot_active" ? (
                    <button onClick={handleTakeover} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#00a884] text-[#111b21] hover:bg-[#00c49a] transition-colors">
                      Assumir conversa
                    </button>
                  ) : (
                    <button onClick={handleReleaseToBot} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#2a3942] text-[#e9edef] hover:bg-[#3b4a54] transition-colors">
                      Devolver ao Bot
                    </button>
                  )}
                </div>

                {/* Human takeover banner */}
                {selectedConv.status === "human_active" && (
                  <div className="px-4 py-2 flex items-center gap-2 text-xs" style={{ background: "#182229", borderBottom: "1px solid #2a2f32" }}>
                    <AlertTriangle className="w-3.5 h-3.5 text-[#f7c948] shrink-0" />
                    <span className="text-[#f7c948]">Modo atendente — O bot retomará após 2 min sem atividade</span>
                  </div>
                )}

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto px-[8%] py-4" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='p' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='1' fill='%2310262e' opacity='0.4'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='200' height='200' fill='%230b141a'/%3E%3Crect width='200' height='200' fill='url(%23p)'/%3E%3C/svg%3E")`,
                  backgroundSize: "200px 200px"
                }}>
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-[#8696a0]" />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {groupedMessages.map((group) => (
                        <div key={group.date}>
                          {/* Date separator */}
                          <div className="flex justify-center my-3">
                            <span className="px-3 py-1 rounded-lg text-[11px] text-[#8696a0] shadow" style={{ background: "#182229" }}>
                              {group.date}
                            </span>
                          </div>
                          {group.msgs.map(msg => (
                            <div key={msg.id} className={cn("flex mb-1", msg.role === "user" ? "justify-start" : "justify-end")}>
                              <div className={cn(
                                "relative max-w-[65%] rounded-lg px-2.5 pt-1.5 pb-4 text-[13.5px] leading-[19px] shadow-sm",
                                msg.role === "user"
                                  ? "rounded-tl-none"
                                  : "rounded-tr-none"
                              )} style={{
                                background: msg.role === "user" ? "#202c33" : "#005c4b",
                                color: "#e9edef",
                              }}>
                                {/* HUMAN_TAKEOVER indicator */}
                                {msg.content.includes("[HUMAN_TAKEOVER]") && (
                                  <div className="flex items-center gap-1 text-[#f7c948] text-[11px] font-medium mb-1 pb-1" style={{ borderBottom: "1px solid #2a3942" }}>
                                    <AlertTriangle className="w-3 h-3" />
                                    Bot solicitou atendente humano
                                  </div>
                                )}
                                <p className="whitespace-pre-wrap break-words">{msg.content.replace("[HUMAN_TAKEOVER]", "").trim()}</p>
                                <span className="absolute bottom-1 right-2 text-[10px] text-[#ffffff99] flex items-center gap-0.5">
                                  {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                  {msg.role === "assistant" && <CheckCheck className="w-3.5 h-3.5 text-[#53bdeb] ml-0.5" />}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Input Bar */}
                <div className="flex items-center gap-2 px-3 py-2 shrink-0" style={{ background: "#202c33" }}>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#2a3942] transition-colors">
                    <Smile className="w-6 h-6 text-[#8696a0]" />
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#2a3942] transition-colors">
                    <Paperclip className="w-6 h-6 text-[#8696a0]" />
                  </button>
                  <div className="flex-1 rounded-lg px-3 py-2.5" style={{ background: "#2a3942" }}>
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                      placeholder="Digite uma mensagem"
                      className="w-full bg-transparent text-[#d1d7db] text-sm placeholder-[#8696a0] outline-none"
                      disabled={sending}
                    />
                  </div>
                  {newMessage.trim() ? (
                    <button
                      onClick={handleSend}
                      disabled={sending}
                      className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#2a3942] transition-colors"
                    >
                      {sending ? <Loader2 className="w-6 h-6 animate-spin text-[#8696a0]" /> : <Send className="w-6 h-6 text-[#8696a0]" />}
                    </button>
                  ) : (
                    <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#2a3942] transition-colors">
                      <Mic className="w-6 h-6 text-[#8696a0]" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Empty State — No conversation selected */
              <div className="flex-1 flex flex-col items-center justify-center gap-4" style={{ background: "#222e35" }}>
                <div className="w-[320px] text-center">
                  <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ background: "#2a3942" }}>
                    <MessageCircle className="w-10 h-10 text-[#8696a0]" />
                  </div>
                  <h3 className="text-[#e9edef] text-xl font-light mb-2">WhatsApp Atendimento</h3>
                  <p className="text-[#8696a0] text-sm leading-relaxed">
                    Gerencie todas as conversas do seu chatbot. Acompanhe respostas automáticas e assuma quando necessário.
                  </p>
                </div>
                <div className="w-64 h-px mt-4" style={{ background: "#2a3942" }} />
                <p className="text-[#667781] text-xs flex items-center gap-1.5">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" stroke="currentColor" strokeWidth="1.5"/><path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  Selecione uma conversa para começar
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
